/**
 * ManualFindingForm -- Trigger button that opens FindingFormDialog for creating a finding
 *
 * Self-contained: manages its own open state.
 * Calls useCreateManualFinding on submit.
 */

import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui";
import { useCreateManualFinding } from "@/packages/features-shared";
import type { Id } from "@/packages/types";
import { FindingFormDialog } from "./FindingFormDialog";
import type { FindingFormValues } from "./FindingFormDialog";

interface ManualFindingFormProps {
  checklistItemId: Id<"checklistItems">;
  questionOrder?: number;
}

export function ManualFindingForm({
  checklistItemId,
  questionOrder,
}: ManualFindingFormProps) {
  const [open, setOpen] = useState(false);
  const createMutation = useCreateManualFinding();

  async function handleSubmit(values: FindingFormValues) {
    await createMutation.mutateAsync({
      checklistItemId,
      title: values.title,
      observation: values.observation,
      recommendation: values.recommendation,
      severity: values.severity,
      questionOrder,
    });
    setOpen(false);
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        icon={Plus}
        onClick={() => setOpen(true)}
        className="h-6 text-[11px] px-2 text-muted hover:text-secondary"
      >
        Add finding
      </Button>

      <FindingFormDialog
        open={open}
        onOpenChange={setOpen}
        mode="create"
        isPending={createMutation.isPending}
        onSubmit={handleSubmit}
      />
    </>
  );
}
